import toast from 'react-hot-toast'
import React, { useCallback, useState } from 'react'
import { Path, UseFormRegister } from 'react-hook-form'

import classNames from 'classnames'
import { uploadAndProcessAudioFiles } from '@src/lib/apiClient'
import { Loader } from '@src/components/icons'
import { WorkFormValues } from '@src/components/WorkForm'
import { AudioUploadResults } from '@src/api/upload'
import { WorkWithDetails } from 'pages/api/work/[id]/index'

export interface HandleAudioTranscode {
  (work: WorkWithDetails, file: File): Promise<void>
}
type AudioUploaderProps = {
  label: Path<WorkFormValues>,
  register: UseFormRegister<WorkFormValues>,
  work?: WorkWithDetails,
  onUploadSuccess: (results: AudioUploadResults) => void,
  disabled: boolean
}
export const AudioUploader: React.FC<AudioUploaderProps> = ({ work, onUploadSuccess, disabled }) => {
  const setFilename = useState('')[1]
  const [jobIsProcessing, setJobIsProcessing] = useState(false)
  const [errorDetail, setErrorDetail] = useState('')

  const handleAudioTranscode: HandleAudioTranscode = useCallback(async (resWork, resFile) => {
    setJobIsProcessing(true)
    setErrorDetail('')
    toast.promise(
      uploadAndProcessAudioFiles({
        workingDirectory: 'audio/uploads',
        cloudDirectory: 'audio',
        file: resFile,
        filename: resFile?.name,
        workId: resWork?.id,
      }),
      {
        loading: 'Processing audio file...',
        error: (error) => {
          setJobIsProcessing(false)
          setFilename('')
          if (error) {
            setErrorDetail(error.message)
            return error.message
          }
          return null
        },
        success: ({ data, error }) => {
          setJobIsProcessing(false)
          setFilename('')
          if (!data || error) {
            toast.error(`Error uploading audio: ${error}`)
            setErrorDetail(String(error))
            return null
          }

          onUploadSuccess(data)
          return 'Successfully updated audio file.'
        },
      },
      {
        loading: {
          duration: 120000,
        },
        success: {
          duration: 1000,
        },
        error: {
          duration: 2000,
        },
      }
    )
  }, [onUploadSuccess, setFilename])

  const handleFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFilename = e.target.files[0].name
    const newFile = e.target.files[0]
    setFilename(newFilename)
    await handleAudioTranscode(work, newFile)
  }, [work, handleAudioTranscode, setFilename])

  return (
    <div className={classNames('audio-upload-form')}>

      {!jobIsProcessing && (
        <div className={classNames('audio-upload-browser')}>
          <input accept=".mp3" type="file" onChange={handleFile} disabled={disabled} />
        </div>
      )}
      {jobIsProcessing && (
        <div className={classNames('audio-processing-info')}>
          <Loader />
          <div>Job is processing. Do not leave page until job completes.</div>
        </div>
      )}
      {errorDetail && <p className="error">{errorDetail}</p>}
    </div>
  )
}
